/* If you edit this file, please remove this header and clean up the resulting eslint errors.
*/
/* eslint-disable
  camelcase,
  import/named,
  import/no-extraneous-dependencies,
  max-len,
  no-console,
  no-param-reassign,
  no-undef,
*/
import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import superagent from 'superagent';
import { stringify } from 'qs';
import './google-signin-button.scss';
import uuidv4 from 'uuid/v4';
import HfReactHelper from 'common/utils/hf-react-helper';
import GoogleSvg from 'common/images/google_icon.svg';
/**
 * A top-level page layout component which tries to fill the entire page.
 * Features a simple header, a small centered card containing child content, and no footer.
 */
export default class GoogleSignInButton extends React.Component {
    static propTypes = {
      clientId: PropTypes.string.isRequired,
      redirectUrl: PropTypes.string,
      getIsOptedIn: PropTypes.func,
      csrfToken: PropTypes.string.isRequired,
      formType: PropTypes.string,
      buttonAttrs: PropTypes.object,
      isSignup: PropTypes.bool,
    };

    // setting default prop for function because this
    // component is used in the SignIn page as well
    static defaultProps = {
      getIsOptedIn: () => {},
      buttonAttrs: {},
    };

    signInButtonRef = React.createRef();

    renderTimeout = null;

    constructor(props) {
      super(props);

      const randomId = uuidv4().substring(0, 6);

      // Initial state
      this.state = {
        id: `signin2-${randomId}`,
        renderStatus: 'waiting',
        hasAuthLoaded: false,
        mockedButtonText: <FormattedMessage id='' defaultMessage='Waiting for Google...' description='sign in with google button' />,
        buttonAttrs: this.props.buttonAttrs,
      };

      // Prebind methods
      this.gapiDidLoad = this.gapiDidLoad.bind(this);
      this.onSignIn = this.onSignIn.bind(this);
      this.onSignInFailure = this.onSignInFailure.bind(this);
    }

    fallbackGoogleButton(message) {
      message += ' Falling back to oauth google button.';
      console.warn(message);
      this.setState({ renderStatus: 'fallback' });
    }

    componentDidMount() {
      this.renderTimeout = setTimeout(() => {
        if (this.state.renderStatus === 'waiting') {
          this.fallbackGoogleButton('Google button didn\'t render in time.');
        }
      }, 3000);

      // We need to determine if gapi has already loaded or if we need to wait for it
      if (window.gapi) {
        this.gapiDidLoad();
      } else {
        // Register for an event letting us know that gapi has been loaded, and then do the thing
        window.addEventListener('hs-gapi-did-load', this.gapiDidLoad);
      }
    }

    componentWillUnmount() {
      clearTimeout(this.renderTimeout);
    }

    shouldComponentUpdate(nextProps, nextState) {
      // We don't want to re-render when state hasn't changed since that would override the content injected by Google
      return nextState.renderStatus !== this.state.renderStatus;
    }

    renderButton(auth2) {
      this.setState({ renderStatus: 'rendering' });
      const signInButton = this.signInButtonRef.current;

      if (signInButton) {
        this.attachSignin(signInButton, auth2);
      }
    }

    attachSignin(element, auth2) {
      auth2.attachClickHandler(element, {}, this.onSignIn, this.onSignInFailure);
    }

    // Things we want to do once gapi has been loaded
    gapiDidLoad() {
      if (this.state.hasAuthLoaded === true) {
        this.renderButton(gapi.auth2.getAuthInstance());
      } else {
        // First we need to load auth2
        gapi.load('auth2', () => {
          // Initialize auth2
          gapi.auth2.init({
            client_id: this.props.clientId,
            scope: 'profile',
          }).then(() => {
            // Tell our Google Auth2 context to sign out just to have a clean slate
            const auth2 = gapi.auth2.getAuthInstance();
            auth2.signOut().then(() => {
              // Tell gapi to render the button
              this.renderButton(auth2);
              this.setState({ hasAuthLoaded: true }); // We only ever want to load auth once.
            });
          }, (error) => {
            this.fallbackGoogleButton(`Error initializing gapi.auth2: ${error.error}, ${error.details}`);
          });
        });
      }
    }

    // Called by gapi.signin2 after successful Google sign-in
    onSignIn(googleUser) {
      const authResponse = googleUser.getAuthResponse();
      window.authResponse = authResponse;
      const id_token = authResponse.id_token;

      const post_body = {
        id_token,
        form_type: this.props.formType,
        redirect_url: this.props.redirectUrl,
        opt_in: this.props.getIsOptedIn(),
        csrf_token: this.props.csrfToken,
      };

      // Send id_token to backend for HelloSign login
      const url = HfReactHelper.urlHelper('account/processGoogleSignIn');
      superagent
        .post(url)
        .type('form')
        .send(stringify(post_body))
        .on('request', () => {
          this.setState({
            renderStatus: 'loading',
            mockedButtonText: <FormattedMessage id='' defaultMessage='Signing you in...' description='sign in with google button' />,
          });
        })
        .end((err, response) => {
          // TODO: Updrade to TypeScript 3.7 or higher to use optional chaining syntax
          // e.g. response?.ok => evaluates to undefined if response is falsey
          if (err || !response || !response.ok) {
            // Error here means some kind of http status error (4xx or 5xx)
            this.onSignInFailure();
          }

          try {
            const response_json = JSON.parse(response.text);
            const redirect_to = response_json.redirect_url;
            window.location.replace(redirect_to);
          } catch (e) {
            console.log(`Exception processing google signin: ${e}`);
            this.onSignInFailure();
          }
        });
    }

    onSignInFailure(result) {
      if (result && result.error === 'popup_closed_by_user') {
        return;
      }

      this.setState({
        renderStatus: 'failure',
        mockedButtonText: <FormattedMessage id='' defaultMessage='Sign-in currently unavailable' description='sign in with google button' />,
      });
    }

    showGoogleButton() {
      return this.state.renderStatus === 'rendering';
    }

    render() {
      return (
        <div>
          <div className="mocked-google-signin-button" style={{ display: !this.showGoogleButton() ? '' : 'none' }}>
            <div className="mocked-google-signin-button--placeholder">{this.state.mockedButtonText}</div>
          </div>
          <button className="google-signin-button__button foundation-exempt" id={this.state.id}
            ref={this.signInButtonRef}
            style={{ display: this.showGoogleButton() ? '' : 'none' }}
            da-category={this.props.buttonAttrs['da-category'] ? this.props.buttonAttrs['da-category'] : null}
            da-action={this.props.buttonAttrs['da-action'] ? this.props.buttonAttrs['da-action'] : null}
            da-label={this.props.buttonAttrs['da-label'] ? this.props.buttonAttrs['da-label'] : null}
          >
            <div className="google-signin-button__left">
              <img src={GoogleSvg} className="google-signin-button__icon" alt="" aria-hidden="true" />
            </div>
            <div className="google-signin-button__right">
              {this.props.isSignup ? (
                <FormattedMessage
                  id=""
                  description="sign up with Google button"
                  defaultMessage="Sign up with Google" />
              ) : (
                <FormattedMessage
                  id=""
                  description="sign in with Google button"
                  defaultMessage="Sign in with Google" />
              )}
            </div>
          </button>
        </div>
      );
    }
}
